import React from 'react';

interface RichTextToken {
  type: 'text' | 'bold' | 'italic' | 'variable' | 'notranslate';
  content: string;
  value?: string;
  noTranslate?: boolean;
}

interface VariableValue {
  value: string;
  noTranslate?: boolean;
}

export const parseRichText = (
  text: string,
  variables: Record<string, string | VariableValue> = {},
  noTranslateTerms: string[] = [],
): RichTextToken[] => {
  const tokens: RichTextToken[] = [];

  const noTranslatePattern =
    noTranslateTerms.length > 0
      ? `(${noTranslateTerms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`
      : '';

  const patterns = [
    '\\*\\*[^*]+\\*\\*', // **bold**
    '\\*[^*]+\\*', // *italic*
    '\\$\\{[^}]+\\}', // ${variable}
  ];

  if (noTranslatePattern) {
    patterns.push(noTranslatePattern);
  }

  const regex = new RegExp(`(${patterns.join('|')})`, 'g');

  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }

    const matchedText = match[0];

    if (matchedText.startsWith('**') && matchedText.endsWith('**')) {
      tokens.push({
        type: 'bold',
        content: matchedText.slice(2, -2),
      });
    } else if (matchedText.startsWith('*') && matchedText.endsWith('*')) {
      tokens.push({
        type: 'italic',
        content: matchedText.slice(1, -1),
      });
    } else if (matchedText.startsWith('${') && matchedText.endsWith('}')) {
      const varName = matchedText.slice(2, -1);
      const variable = variables[varName];

      if (typeof variable === 'object' && variable !== null) {
        tokens.push({
          type: 'variable',
          content: varName,
          value: variable.value || matchedText,
          noTranslate: variable.noTranslate,
        });
      } else {
        tokens.push({
          type: 'variable',
          content: varName,
          value: (variable as string) || matchedText,
        });
      }
    } else if (noTranslateTerms.includes(matchedText)) {
      tokens.push({
        type: 'notranslate',
        content: matchedText,
      });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    tokens.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  return tokens;
};

export const RichTextRenderer: React.FC<{
  text: string;
  variables?: Record<string, string | VariableValue>;
  boldStyle?: React.CSSProperties;
  italicStyle?: React.CSSProperties;
  noTranslateTerms?: string[];
  noTranslateClass?: string;
}> = ({
  text,
  variables = {},
  boldStyle,
  italicStyle,
  noTranslateTerms = [],
  noTranslateClass = 'notranslate',
}) => {
  const tokens = parseRichText(text, variables, noTranslateTerms);

  return (
    <>
      {tokens.map((token, index) => {
        switch (token.type) {
          case 'bold':
            return (
              <strong key={index} style={boldStyle}>
                {token.content}
              </strong>
            );
          case 'italic':
            return (
              <em key={index} style={italicStyle}>
                {token.content}
              </em>
            );
          case 'variable':
            return (
              <strong
                key={index}
                style={boldStyle}
                className={token.noTranslate ? noTranslateClass : undefined}
              >
                {token.value}
              </strong>
            );
          case 'notranslate':
            return (
              <span key={index} className={noTranslateClass}>
                {token.content}
              </span>
            );
          default:
            return <span key={index}>{token.content}</span>;
        }
      })}
    </>
  );
};
